import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

function UserIdEntry({ userId, setUserId, userEmail, setUserEmail, handleUserIdSubmit, error, authConfig }) {
  const [isLoading, setIsLoading] = useState(false);

  const isSsoEnabled = authConfig?.sso_enabled;
  const ssoUrl = authConfig?.sso_url;
  const callbackPath = authConfig?.callback_path || '/';

  const handleSsoLogin = () => {
    if (!ssoUrl) return;
    const returnUrl = `${window.location.origin}${callbackPath}`;
    window.location.href = `${ssoUrl}?return_url=${encodeURIComponent(returnUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    await handleUserIdSubmit(e);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-primary-50/30 to-secondary-50/30 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-secondary-400/20 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-6xl"
      >
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="card p-8 lg:p-10"
            >
              <div className="mb-8 flex justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl">
                    <ShieldCheckIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                      AI INCOSE Recommendation [Requirements]
                    </h1>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Secure workspace for requirement analysis
                    </p>
                  </div>
                </div>
              </div>

              {isSsoEnabled && (
                <div className="space-y-4">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800"
                    >
                      <p className="text-sm text-danger-700 dark:text-danger-400">
                        {error}
                      </p>
                    </motion.div>
                  )}
                  <button
                    onClick={handleSsoLogin}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <ShieldCheckIcon className="w-5 h-5" />
                    <span>Sign in with Stellantis SSO</span>
                  </button>
                </div>
              )}

              {!isSsoEnabled && (
                <form onSubmit={onSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="user-id" className="input-label flex items-center gap-2">
                      <UserIcon className="w-4 h-4" />
                      User ID
                      <span className="text-danger-500">*</span>
                    </label>
                    <input
                      id="user-id"
                      type="text"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="Enter your user ID (e.g., T1234AB)"
                      className="input-field mt-1"
                      autoFocus
                      required
                    />
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      Your unique identifier for accessing your workspace
                    </p>
                  </div>

                  <div>
                    <label htmlFor="user-email" className="input-label flex items-center gap-2">
                      <EnvelopeIcon className="w-4 h-4" />
                      Email
                      <span className="text-neutral-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      id="user-email"
                      type="email"
                      value={userEmail || ''}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="input-field mt-1"
                    />
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800"
                    >
                      <p className="text-sm text-danger-700 dark:text-danger-400">
                        {error}
                      </p>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={!userId.trim() || isLoading}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Accessing Workspace...</span>
                      </>
                    ) : (
                      <>
                        <span>Continue to Workspace</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default UserIdEntry;
